/**
 * Tooltip.jsx
 *
 * @Author: jruif
 * @Date: 2017/7/14 下午3:18
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ResizeObserver from 'resize-observer-polyfill';
import extend from 'deep-extend';
import Box from '../box';
import omit from '../util/omit';
import throttle from '../util/throttle';
import addEventListener from '../util/addEventListener';
import { isBoolean } from '../util/isType';

export const POSITIONS = [
  'top',
  'topLeft',
  'topRight',
  'bottom',
  'bottomLeft',
  'bottomRight',
  'left',
  'leftTop',
  'leftBottom',
  'right',
  'rightTop',
  'rightBottom',
];

export const defaultProps = {
  prefixCls: 'ne-tooltip',
  position: 'top',
  isOpen: false,
  isCloseOnDocumentClick: true,
  isScrollClose: false,
  autoAdjustPosition: false,
  mouseEnterDelay: 50,
  mouseLeaveDelay: 100,
  trigger: 'hover',
  translate: 5,
  animationTime: 0, // ms
  isCloseOnTriggerClick: undefined,
};

/**
 * Tooltip
 * */
class Tooltip extends React.Component {
  static propTypes = {
    /** @ignore */
    children: PropTypes.node,
    /**
     * 触发方式
     * */
    trigger: PropTypes.oneOf(['click', 'hover', 'focus', 'none']),
    /**
     * 提示框标题
     * */
    title: PropTypes.node,
    /**
     * 触发提示框的Node，接受的数据类型是PropTypes.node
     * */
    content: PropTypes.node,
    /**
     * 用于手动控制提示框显隐
     * */
    isOpen: PropTypes.bool,
    /**
     * 提示框是否在页面滚动的时候关闭
     * */
    isScrollClose: PropTypes.bool,
    // isCacheInNodeTree: PropTypes.bool,
    /**
     * 点击提示框外层是否关闭
     * */
    isCloseOnDocumentClick: PropTypes.bool,
    /**
     * 气泡被遮挡时是否自动调整定位
     * */
    autoAdjustPosition: PropTypes.bool,
    /**
     * 显示样式
     * */
    style: PropTypes.object,
    /**
     * 显示类名
     * */
    className: PropTypes.string,
    /**
     * 提示框位置
     * */
    position: PropTypes.oneOf(POSITIONS),
    /** 提示框距离trigger元素的偏移距离，单位是像素（px） */
    translate: PropTypes.number,
    /** 鼠标移入时延迟时间 */
    mouseEnterDelay: PropTypes.number,
    /** 鼠标移出时延迟时间 */
    mouseLeaveDelay: PropTypes.number,
    /** 动画执行时间 */
    animationTime: PropTypes.number,
    /** 关闭回调 */
    onClose: PropTypes.func,
    /** 滚动容器dom节点 */
    scrollContainer: PropTypes.element,
    /** 点击关闭弹框 */
    isCloseOnTriggerClick: PropTypes.bool
  };

  static defaultProps = defaultProps;

  constructor(props, context) {
    super(props, context);
    this.hasPosition = false;
    this.state = {
      isOpen: props.isOpen,
      style: {
        top: null,
        left: null,
        width: null,
        transform: null,
        opacity: 0,
        display: 'none',
      },
      arrow: {
        left: null,
        top: null,
      },
      position: props.position,
    };
    this.updateTriggerEvent();
  }

  componentDidMount() {
    if (this.state.isOpen) {
      setTimeout(this.handleOpen);
    }
    this.resizeObserver = new ResizeObserver(entries => {
      entries.forEach(this.setPosition);
    });
  }

  componentWillReceiveProps(newProps) {
    if (newProps.isOpen !== this.props.isOpen) {
      this.setState({
        isOpen: newProps.isOpen,
      });
    }
    this.updateTriggerEvent();
  }
  // shouldComponentUpdate(nextProps, nextState) {
  //     // 打开／将要状态下 或 content 发生改变
  //     return this.state.isOpen || nextState.isOpen || this.props.content !== nextProps.content;
  // }

  updateTriggerEvent = () => {
    const { trigger, isCloseOnTriggerClick } = this.props;
    this.triggerEvent = {
      /** 当trigger被点击时，是否打开 */
      isOpenOnTriggerClick: trigger === 'click',

      /** 在trigger上点击是否关闭 */
      isCloseOnTriggerClick: isCloseOnTriggerClick !== undefined ? isCloseOnTriggerClick : trigger === 'click',

      /** 当trigger被focus时，是否打开 */
      isOpenOnTriggerFocus: trigger === 'focus',

      /** 在trigger上失焦是否关闭 */
      isCloseOnTriggerBlur: trigger === 'focus',

      /** 当trigger被Mouse Enter时，是否打开 */
      isOpenOnTriggerMouseEnter: trigger === 'hover',

      /** 在trigger上Mouse Leave是否关闭 */
      isCloseOnTriggerMouseLeave: trigger === 'hover',

      /** 在box上Mouse Leave是否关闭 */
      isCloseOnBoxMouseLeave: trigger === 'hover',
    };
  };

  setPosition = () => {
    if (!this.state.isOpen || this.hasPosition || !(this.triggerNode && this.tooltipClientRect)) {
      return;
    }
    this.detectBoundary();
    const tooltipClientRect = this.tooltipClientRect;
    const arrowClientRect = this.arrowClientRect;
    const clientRect = this.triggerNode.getBoundingClientRect();
    const { pageYOffset, pageXOffset /* , innerWidth */ } = window;
    const { clientWidth /* , clientHeight, offsetHeight */ } = document.documentElement;
    const { translate, autoWidth, fixedWidth } = this.props;
    const { position } = this.state;

    // 计算坐标
    const index = POSITIONS.indexOf(position);
    const nowStyle = { left: null, top: null, right: null, bottom: null, opacity: 1 };
    const arrow = { top: null, left: null };

    let transformX = 0;

    let transformY = 0;

    switch (index) {
      case -1:
      case 0: // top
        transformX = -((tooltipClientRect.width || fixedWidth) - clientRect.width) / 2;
        // transformY = -tooltipClientRect.height + translate;
        transformY = -clientRect.height + translate;
        nowStyle.transform = `translate(0, -${translate}px)`;
        nowStyle.bottom = -Math.round(pageYOffset + clientRect.bottom + transformY);
        nowStyle.left = Math.round(pageXOffset + clientRect.left + transformX);
        break;
      case 1: // topLeft
        transformY = -clientRect.height + translate;
        nowStyle.transform = `translate(0, -${translate}px)`;
        nowStyle.bottom = -Math.round(pageYOffset + clientRect.bottom + transformY);
        nowStyle.left = Math.round(clientRect.left + pageXOffset);
        arrow.left = Math.round((clientRect.width - arrowClientRect.width) / 2);
        break;
      case 2: // topRight
        // transformX = -tooltipClientRect.width + clientRect.width;
        transformY = -clientRect.height + translate;
        nowStyle.transform = `translate(0, -${translate}px)`;
        nowStyle.bottom = -Math.round(pageYOffset + clientRect.bottom + transformY);
        nowStyle.right = Math.round(clientWidth - pageXOffset - clientRect.right);
        arrow.left = Math.round(
          tooltipClientRect.width - (clientRect.width + arrowClientRect.width) / 2,
        );
        break;
      case 3: // bottom
        transformX = (-(tooltipClientRect.width || fixedWidth) + clientRect.width) / 2;
        transformY = clientRect.height - translate;
        nowStyle.top = Math.round(pageYOffset + clientRect.top + transformY);
        nowStyle.left = Math.round(pageXOffset + clientRect.left + transformX);
        nowStyle.transform = `translate(0, ${translate}px)`;
        break;
      case 4: // bottomLeft
        transformY = clientRect.height - translate;
        nowStyle.transform = `translate(0, ${translate}px)`;
        nowStyle.top = Math.round(pageYOffset + clientRect.top + transformY);
        nowStyle.left = Math.round(clientRect.left + pageXOffset + transformX);
        arrow.left = Math.round((clientRect.width - arrowClientRect.width) / 2);
        break;
      case 5: // bottomRight
        transformY = clientRect.height - translate;
        nowStyle.transform = `translate(0, ${translate}px)`;
        nowStyle.top = Math.round(pageYOffset + clientRect.top + transformY);
        nowStyle.right = Math.round(clientWidth - pageXOffset - clientRect.right);
        arrow.left = Math.round(
          tooltipClientRect.width - (clientRect.width + arrowClientRect.width) / 2,
        );
        break;
      case 6: // left
        transformY = -(tooltipClientRect.height - clientRect.height) / 2;
        nowStyle.top = Math.round(pageYOffset + clientRect.top + transformY);
        nowStyle.right = Math.round(clientWidth - pageXOffset - clientRect.left - translate);
        nowStyle.transform = `translate(-${translate}px, 0)`;
        break;
      case 7: // leftTop
        nowStyle.top = Math.round(pageYOffset + clientRect.top);
        nowStyle.right = Math.round(clientWidth - pageXOffset - clientRect.left - translate);
        nowStyle.transform = `translate(-${translate}px, 0)`;
        arrow.top = Math.round((clientRect.height - arrowClientRect.height) / 2);
        break;
      case 8: // leftBottom
        // transformY = -clientRect.height + translate;
        // transformX = -translate;
        nowStyle.bottom = -Math.round(pageYOffset + clientRect.bottom);
        nowStyle.right = Math.round(clientWidth - pageXOffset - clientRect.left - translate);
        nowStyle.transform = `translate(-${translate}px, 0)`;
        arrow.bottom = Math.round((clientRect.height - arrowClientRect.height) / 2);
        break;
      case 9: // right
        transformY = -(tooltipClientRect.height - clientRect.height) / 2;
        nowStyle.top = Math.round(pageYOffset + clientRect.top + transformY);
        nowStyle.left = Math.round(pageXOffset + clientRect.right);
        nowStyle.transform = `translate(${translate}px, 0)`;
        transformX = clientRect.width - translate;
        break;
      case 10: // rightTop
        transformX = clientRect.width - translate;
        nowStyle.top = Math.round(pageYOffset + clientRect.top);
        nowStyle.left = Math.round(pageXOffset + clientRect.left + transformX);
        nowStyle.transform = `translate(${translate}px, 0)`;
        arrow.top = Math.round((clientRect.height - arrowClientRect.height) / 2);
        break;
      case 11: // rightBottom
        transformX = clientRect.width - translate;
        nowStyle.bottom = -Math.round(pageYOffset + clientRect.bottom);
        nowStyle.left = Math.round(pageXOffset + clientRect.left + transformX);
        nowStyle.transform = `translate(${translate}px, 0)`;
        arrow.bottom = Math.round((clientRect.height - arrowClientRect.height) / 2);
        break;
      default:
        return;
    }

    // bug fixed: 在ReactDOM.unstable_renderSubtreeIntoContainer挂载的时候
    // tooltip窗口内文字会莫名其妙的换行，导致首次定位出现错误
    // nowStyle.width = tooltipClientRect.width;

    if (autoWidth) {
      nowStyle.width = this.triggerNode.clientWidth;
    }
    this.setState({
      style: omit(nowStyle, value => value === null),
      arrow,
    });
    // this.hasPosition = true;
  };

  // 检测边界
  detectBoundary = () => {
    const { position, autoAdjustPosition } = this.props;
    if (!autoAdjustPosition && this.triggerNode !== null) {
      return;
    }
    const { clientWidth, clientHeight } = document.documentElement;
    const clientRect = this.triggerNode.getBoundingClientRect();
    const tooltipClientRect = this.tooltipNode && this.tooltipNode.getBoundingClientRect();
    const boundary = position.split(/(?=[A-Z])/g);
    let [first, second = 'center'] = boundary;
    if (!tooltipClientRect) {
      return;
    }

    const horizontal = second => {
      let isInit = 0;
      second = second.toLowerCase();
      const func = {
        left: () => {
          isInit++;
          return clientRect.left + tooltipClientRect.width < clientWidth || isInit > 2
            ? 'Left'
            : func.center();
        },
        center: () => {
          isInit++;
          const ban = (tooltipClientRect.width - clientRect.width) / 2;
          if (tooltipClientRect.width < clientWidth) {
            if ((clientRect.right + ban < clientWidth && clientRect.left > ban) || isInit > 2) {
              return 'center';
            }
            if (clientRect.left + clientRect.width / 2 > clientWidth / 2) {
              return func.right();
            }
            return func.left();
          }
          return 'center';
        },
        right: () => {
          isInit++;
          return clientRect.right > tooltipClientRect.width || isInit > 2 ? 'Right' : func.center();
        },
      };
      return func[second]();
    };

    const vertical = second => {
      let isInit = 0;
      second = second.toLowerCase();
      const func = {
        top: () => {
          isInit++;
          return clientRect.top + tooltipClientRect.height < clientHeight || isInit > 3
            ? 'Top'
            : func.center();
        },
        center: () => {
          isInit++;
          const ban = (tooltipClientRect.height - clientRect.height) / 2;
          if (tooltipClientRect.height < clientHeight) {
            if ((clientRect.bottom + ban < clientHeight && clientRect.top > ban) || isInit > 3) {
              return 'center';
            }
            if (clientRect.top + clientRect.width / 2 > clientHeight / 2) {
              return func.bottom();
            }
            return func.top();
          }
          return 'center';
        },
        bottom: () => {
          isInit++;
          return clientRect.bottom > tooltipClientRect.height || isInit > 3 ? 'Bottom' : func.top();
        },
      };
      return func[second]();
    };

    switch (first) {
      case 'top':
        if (clientRect.top - tooltipClientRect.height < 0) {
          first = 'bottom';
        }
        second = horizontal(second);
        break;
      case 'left':
        if (clientRect.left - tooltipClientRect.width < 0) {
          first = 'right';
        }
        // top or bottom
        second = vertical(second);
        break;
      case 'bottom':
        if (tooltipClientRect.height + clientRect.bottom > clientHeight) {
          first = 'top';
        }
        second = horizontal(second);
        break;
      case 'right':
        if (tooltipClientRect.width + clientRect.left > clientWidth) {
          first = 'left';
        }
        second = vertical(second);
        break;
      default:
        break;
    }

    if (first) {
      this.setState({
        position: [first, second.replace('center', '')].join(''),
      });
    }
  };

  handleOpen = e => {
    const { onOpen, trigger } = this.props;
    if (trigger === 'none') {
      return;
    }

    let target;
    if (this.contentNode) {
      target = this.contentNode;
    } else {
      target = e.currentTarget;
    }
    if (this.resizeObserver) {
      this.resizeObserver.observe(target);
    }
    this.triggerNode = target;

    this.setState({ isOpen: true }, () => {
      this.setPosition();
    });

    if (onOpen) {
      return onOpen(e, this.props);
    }
  };

  handleClose = e => {
    const { onClose } = this.props;
    this.setState({
      isOpen: false,
      style: {},
    });
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (onClose) {
      return onClose(e, this.props);
    }
    // .then(() => prom)
  };

  handleMount = e => {
    const { onMount, isScrollClose, scrollContainer } = this.props;
    if (onMount) onMount(e, this.props);
    this.onResize = addEventListener(window, 'resize', throttle(this.setPosition, 60));
    this.onScroll = addEventListener(
      scrollContainer || window,
      'scroll',
      isScrollClose
        ? e => {
            this.handleClose(e);
            if (this.onScroll) {
              this.onScroll.remove();
              this.onScroll = null;
            }
          }
        : // 16ms 防止抖动
          throttle(this.setPosition, scrollContainer ? 16 : 60),
    );
  };

  handleUnmount = e => {
    const { onUnmount } = this.props;
    // this.hasPosition = false;
    this.triggerNode = null;
    this.tooltipClientRect = null;
    this.arrowClientRect = null;
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.onScroll) {
      this.onScroll.remove();
      this.onScroll = null;
    }
    if (this.onResize) {
      this.onResize.remove();
      this.onResize = null;
    }

    this.setState({
      style: {},
    });
    if (onUnmount) onUnmount(e, this.props);
  };

  handleRef = tooltip => {
    const { prefixCls } = this.props;
    this.tooltipNode = tooltip;
    this.tooltipClientRect = tooltip ? tooltip.getBoundingClientRect() : null;
    this.arrowClientRect = tooltip
      ? tooltip.querySelector(`.${prefixCls}-arrow`).getBoundingClientRect()
      : null;
  };

  handleContentRef = content => {
    this.contentNode = content;
  };

  render() {
    const {
      content,
      trigger,
      prefixCls,
      children,
      className,
      title,
      style,
      boxClassName,
      animationTime,
      isOpen,
      ...other
    } = this.props;
    const { position, isOpen: isOpenState, style: stateStyle, arrow } = this.state;

    const omitProps = [];

    Object.keys(this.triggerEvent).forEach(key => {
      omitProps.push(key);
      if (isBoolean(other[key])) {
        this.triggerEvent[key] = other[key];
      }
    });

    const classes = classNames(
      prefixCls,
      {
        [`${prefixCls}-position-${position}`]: position,
      },
      className,
    );

    const tooltip = (
      <div
        className={classes}
        style={Object.assign(
          {
            transition: `all ${animationTime / 1000}s`,
          },
          stateStyle,
          isOpenState ? { display: '' } : { display: 'none' },
          style,
        )}
        ref={this.handleRef}>
        <div className={`${prefixCls}-arrow`} style={arrow} />
        {stateStyle.display === 'none' ? null : (
          <div className={`${prefixCls}-inner`}>
            {title && <div className={`${prefixCls}-header`}>{title}</div>}
            {children && <div className={`${prefixCls}-content`}>{children}</div>}
          </div>
        )}
      </div>
    );
    return (
      <Box
        {...omit(other, [
          'children',
          'className',
          'prefixCls',
          'trigger',
          'isOpen',
          'style',
          'position',
          ...omitProps,
        ])}
        {...omit(this.triggerEvent, [])}
        trigger={content}
        triggerRef={this.handleContentRef}
        className={classNames(`${prefixCls}-box`, boxClassName)}
        isOpen={isOpenState}
        onOpen={this.handleOpen}
        onClose={this.handleClose}
        onMount={this.handleMount}
        onUnmount={this.handleUnmount}>
        {tooltip}
      </Box>
    );
  }
}

export default Tooltip;



// WEBPACK FOOTER //
// ./src/tooltip/Tooltip.jsx